/**
 * Admin Home — org-level command center for platform ADMIN only.
 *
 * Build-fresh (does not reuse views/home/AdminHome.tsx).
 * Default Admin landing when entered via /home (HomeRoute) or post-login
 * (defaultPostLoginPath). Route remains ADMIN-gated (RequireAdminInline + page).
 *
 * Width: match /org-dashboard and /administration (full parent width;
 * no max-w-* constraint that would diverge on wide monitors).
 *
 * Needs your attention is PINNED (always on). Other sections follow the
 * per-Admin Customize home layout. Snapshot openExceptions are not rendered
 * on this page (the Administration workspaces table shows them).
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Link, Navigate } from "react-router-dom";

import { type AdminHomeSectionId } from "./adminHomeLayout";
import { AdminHomeContextHeader } from "./components/AdminHomeContextHeader";
import { AdminHomeNeedsAttention } from "./components/AdminHomeNeedsAttention";
import { AdminHomePeopleByRole } from "./components/AdminHomePeopleByRole";
import { AdminHomeProjectsAtRisk } from "./components/AdminHomeProjectsAtRisk";
import { CustomizeAdminHomeDialog } from "./components/CustomizeAdminHomeDialog";
import {
  ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF,
  AdminHomePrimaryActions,
} from "./components/AdminHomePrimaryActions";
import { countPeopleByRole, rosterIncludesCurrentUser } from "./peopleByRole";
import { deriveSetupChecklist } from "./setupChecklist";
import { useAdminHomeLayout } from "./useAdminHomeLayout";

import { Button } from "@/components/ui/button/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/card/Card";
import { InlineLoadingState } from "@/components/ui/states";
import { intentColors, spacing, typography } from "@/design";
import {
  administrationApi,
  type GatesAttention,
  type GovernanceDecision,
  type OrgInviteListItem,
  type WorkspaceSnapshotRow,
} from "@/features/administration/api/administration.api";
import { InviteMembersDialog } from "@/features/administration/components/InviteMembersDialog";
import { useOrgHomeState } from "@/features/organizations/useOrgHomeState";
import { listOrgUsersPage } from "@/features/workspaces/workspace.api";
import { WorkspaceCreateModal } from "@/features/workspaces/WorkspaceCreateModal";
import { request } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/state/AuthContext";
import { useWorkspaceStore } from "@/state/workspace.store";
import { useAdminHomeCustomizeStore } from "@/stores/adminHomeCustomizeStore";
import { isPlatformAdmin } from "@/utils/access";

const linkPrimaryClass = cn(
  "inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground",
  "hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

const linkOutlineClass = cn(
  "inline-flex h-9 items-center justify-center rounded-md border border-input bg-white px-3 text-xs font-medium text-slate-800",
  "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

/** B21: widget stays in source and is not rendered. Approvals is unchanged. */
const SHOW_ADMIN_HOME_EXCEPTIONS = false;

/** Rows fetched for the Needs-attention list; the COUNT comes from the total. */
const PENDING_DECISIONS_PAGE_LIMIT = 5;

export default function AdminHomePage() {
  const { user, loading: authLoading } = useAuth();
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace);
  const {
    isLoading: onboardingLoading,
    isError: onboardingError,
    status: onboardingStatus,
  } = useOrgHomeState();

  const [workspaces, setWorkspaces] = useState<WorkspaceSnapshotRow[] | null>(
    null,
  );
  const [workspacesFailed, setWorkspacesFailed] = useState(false);
  const [pendingDecisions, setPendingDecisions] = useState<
    GovernanceDecision[] | null
  >(null);
  const [pendingDecisionsTotal, setPendingDecisionsTotal] = useState<
    number | null
  >(null);
  const [pendingInvites, setPendingInvites] = useState<
    OrgInviteListItem[] | null
  >(null);
  const [orgUsers, setOrgUsers] = useState<
    Array<{ id?: string; role?: string; platformRole?: string }> | null
  >(null);
  const [orgUsersFailed, setOrgUsersFailed] = useState(false);
  // The organization's real member count; the roster itself is one page (<=100).
  const [orgUsersTotal, setOrgUsersTotal] = useState<number | null>(null);
  const [gates, setGates] = useState<GatesAttention | null>(null);
  const [gatesFailed, setGatesFailed] = useState(false);
  const [projectsAtRisk, setProjectsAtRisk] = useState<
    number | { status: "unavailable" } | null
  >(null);
  const [projectsAtRiskFailed, setProjectsAtRiskFailed] = useState(false);
  const [pendingDecisionsFailed, setPendingDecisionsFailed] = useState(false);
  const [pendingInvitesFailed, setPendingInvitesFailed] = useState(false);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [sectionLoading, setSectionLoading] = useState(true);
  const layout = useAdminHomeLayout();
  const customizeOpen = useAdminHomeCustomizeStore((st) => st.isOpen);
  const closeCustomize = useAdminHomeCustomizeStore((st) => st.close);

  const loadSections = useCallback(async () => {
    if (!user || !isPlatformAdmin(user)) return;
    setSectionLoading(true);

    const orgId = user.organizationId;
    const results = await Promise.allSettled([
      administrationApi.getWorkspaceSnapshot({ page: 1, limit: 20 }),
      administrationApi.listPendingDecisions({
        page: 1,
        limit: PENDING_DECISIONS_PAGE_LIMIT,
      }),
      orgId
        ? administrationApi.listOrgInvites(orgId)
        : Promise.resolve([] as OrgInviteListItem[]),
      listOrgUsersPage(),
      orgId
        ? request.get<{ name?: string; data?: { name?: string } }>(
            `/organizations/${orgId}`,
          )
        : Promise.resolve(null),
      // A missing/throwing read is a rejected promise -> "Unavailable".
      Promise.resolve().then(() => administrationApi.getGatesAttention()),
      Promise.resolve().then(() => administrationApi.getProjectsAtRiskCount()),
    ]);

    if (results[0].status === "fulfilled") {
      setWorkspaces(results[0].value.data);
      setWorkspacesFailed(false);
    } else {
      setWorkspaces(null);
      setWorkspacesFailed(true);
    }

    // The number is the server's TOTAL. A page's length is only exact when the
    // page came back short of its limit; a FULL page with no total proves
    // nothing about the queue's size, so it is Unavailable, never "5".
    const pending = results[1].status === "fulfilled" ? results[1].value : null;
    const pendingTotal = pending
      ? typeof pending.meta?.total === "number"
        ? pending.meta.total
        : pending.data.length < PENDING_DECISIONS_PAGE_LIMIT
          ? pending.data.length
          : null
      : null;
    if (pending && pendingTotal !== null) {
      setPendingDecisions(pending.data);
      setPendingDecisionsTotal(pendingTotal);
      setPendingDecisionsFailed(false);
    } else {
      // A failed read is "unavailable", never an empty queue.
      setPendingDecisions(null);
      setPendingDecisionsTotal(null);
      setPendingDecisionsFailed(true);
    }

    if (results[2].status === "fulfilled") {
      setPendingInvites(results[2].value);
      setPendingInvitesFailed(false);
    } else {
      setPendingInvites(null);
      setPendingInvitesFailed(true);
    }

    if (
      results[3].status === "fulfilled" &&
      rosterIncludesCurrentUser(results[3].value.users, user.id)
    ) {
      setOrgUsers(results[3].value.users);
      setOrgUsersTotal(results[3].value.total);
      setOrgUsersFailed(false);
    } else {
      // Failed, or a list that does not even contain the signed-in Admin: it
      // cannot be the whole organization, so it is Unavailable, not 0 / 0 / 0.
      setOrgUsers(null);
      setOrgUsersTotal(null);
      setOrgUsersFailed(true);
    }

    if (results[4].status === "fulfilled" && results[4].value) {
      const org = results[4].value;
      const name = org.name ?? org.data?.name;
      setOrganizationName(typeof name === "string" && name.trim() ? name : null);
    } else {
      setOrganizationName(null);
    }

    if (results[5].status === "fulfilled" && results[5].value) {
      setGates(results[5].value);
      setGatesFailed(false);
    } else {
      setGates(null);
      setGatesFailed(true);
    }

    if (results[6].status === "fulfilled" && results[6].value !== undefined) {
      setProjectsAtRisk(results[6].value);
      setProjectsAtRiskFailed(false);
    } else {
      setProjectsAtRisk(null);
      setProjectsAtRiskFailed(true);
    }

    setSectionLoading(false);
  }, [user]);

  useEffect(() => {
    void loadSections();
  }, [loadSections]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlatformAdmin(user)) return <Navigate to="/home" replace />;

  // null = the roster could not be read: the invite step is then unknown, not done.
  const orgMemberCount =
    !orgUsersFailed && orgUsers !== null ? (orgUsersTotal ?? orgUsers.length) : null;

  const showChecklist =
    !onboardingError && !onboardingLoading && onboardingStatus != null;
  const checklist = showChecklist
    ? deriveSetupChecklist(onboardingStatus, { orgMemberCount })
    : [];
  const checklistDone = checklist.filter((item) => item.done).length;

  const showWorkspaces = !workspacesFailed && workspaces !== null;
  const workspaceCount = workspaces?.length ?? 0;
  const peopleCounts =
    !orgUsersFailed && orgUsers !== null ? countPeopleByRole(orgUsers) : null;

  const sectionNodes: Record<AdminHomeSectionId, ReactNode> = {
    quick_actions: (
      <AdminHomePrimaryActions
        visibleActions={layout.actions}
        onCreateWorkspace={() => setCreateWorkspaceOpen(true)}
        onInviteMember={() => setInviteOpen(true)}
      />
    ),
    workspaces: showWorkspaces && (
      <Card data-testid="admin-home-workspaces">
        <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-slate-100 p-5">
          <div className="space-y-1">
            <h2 className={typography.sectionTitle}>Workspaces</h2>
            <p className={typography.muted}>
              {workspaceCount === 0
                ? "No workspaces in this organization yet."
                : `${workspaceCount} workspace${workspaceCount === 1 ? "" : "s"} from your organization snapshot.`}
            </p>
          </div>
        </CardHeader>
        <CardBody className="p-5">
          {workspaceCount === 0 ? (
            <div
              className={cn(
                "rounded-lg border border-dashed p-8 text-center",
                intentColors.neutral.bg,
                intentColors.neutral.border,
              )}
              data-testid="admin-home-workspaces-empty"
            >
              <p className={cn(typography.sectionTitle, "text-slate-900")}>
                No workspaces yet
              </p>
              <p className={cn("mx-auto mt-2 max-w-md", typography.muted)}>
                Start with onboarding or pick a template so the first workspace
                is set up — not left unexplained and empty.
              </p>
              <div
                className={cn(
                  "mt-5 flex flex-wrap justify-center",
                  spacing.inlineGap,
                )}
              >
                <Link
                  to="/onboarding"
                  className={linkPrimaryClass}
                  data-testid="admin-home-workspaces-onboarding-cta"
                >
                  Continue onboarding
                </Link>
                <Link
                  to="/templates"
                  className={linkOutlineClass}
                  data-testid="admin-home-workspaces-templates-cta"
                >
                  Open templates
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {workspaces!.map((ws) => (
                <Link
                  key={ws.workspaceId}
                  to={`/workspaces/${ws.workspaceId}/home`}
                  className={cn(
                    "rounded-lg border bg-white p-4 shadow-sm transition",
                    intentColors.neutral.border,
                    "hover:border-slate-300 hover:bg-slate-50",
                  )}
                  data-testid={`admin-home-workspace-card-${ws.workspaceId}`}
                >
                  <p className={cn(typography.sectionTitle, "text-slate-900")}>
                    {ws.workspaceName}
                  </p>
                  <p className={cn("mt-1.5", typography.muted)}>
                    {ws.projectCount}{" "}
                    {ws.projectCount === 1 ? "project" : "projects"}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    ),
    setup_checklist: showChecklist ? (
      <Card
        data-testid="admin-home-checklist"
        data-collapsed={checklistOpen ? "false" : "true"}
      >
        <CardHeader className="flex-row items-center justify-between space-y-0 p-5 pb-3">
          <div className="space-y-1">
            <h2 className={typography.sectionTitle}>Setup checklist</h2>
            <p className={typography.muted}>
              {checklistDone} of {checklist.length} complete
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={checklistOpen}
            data-testid="admin-home-checklist-toggle"
            onClick={() => setChecklistOpen((open) => !open)}
          >
            {checklistOpen ? "Hide steps" : "Show steps"}
          </Button>
        </CardHeader>
        <CardBody
          className={
            checklistOpen
              ? "space-y-3 border-t border-slate-100 p-5"
              : "hidden"
          }
        >
          <ul className={spacing.stack}>
            {checklist.map((item) => (
              <li
                key={item.id}
                className={cn(
                  "flex items-center gap-3 rounded-md border px-3 py-2.5",
                  item.done
                    ? cn(
                        intentColors.success.bg,
                        intentColors.success.borderSubtle,
                      )
                    : cn(
                        intentColors.neutral.bg,
                        intentColors.neutral.borderSubtle,
                      ),
                )}
                data-testid={`admin-home-checklist-${item.id}`}
                data-done={item.done ? "true" : "false"}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    item.done
                      ? intentColors.success.badge
                      : intentColors.neutral.badge,
                  )}
                  aria-hidden
                >
                  {item.done ? "✓" : "○"}
                </span>
                <span
                  className={cn(
                    typography.body,
                    item.done && "text-slate-500 line-through",
                  )}
                >
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
          {checklist.some((i) => !i.done) && (
            <Link
              to="/onboarding"
              className={cn(linkPrimaryClass, "mt-1")}
              data-testid="admin-home-checklist-cta"
            >
              Continue setup
            </Link>
          )}
        </CardBody>
      </Card>
    ) : null,
    projects_at_risk: (
      <AdminHomeProjectsAtRisk
        count={projectsAtRisk}
        failed={projectsAtRiskFailed}
        loading={sectionLoading}
      />
    ),
    people_by_role: (
      <AdminHomePeopleByRole
        counts={peopleCounts}
        failed={orgUsersFailed}
        loading={sectionLoading}
        totalMembers={orgMemberCount}
        loadedMembers={orgUsers?.length ?? null}
      />
    ),
  };

  const showExceptions =
    SHOW_ADMIN_HOME_EXCEPTIONS &&
    pendingDecisionsTotal !== null &&
    pendingDecisions !== null;

  return (
    <div
      className={cn(spacing.page, "space-y-6")}
      data-testid="admin-home-page"
    >
      <AdminHomeContextHeader
        firstName={user.firstName}
        organizationName={organizationName}
      />

      <AdminHomeNeedsAttention
        decisions={pendingDecisions}
        decisionsTotal={pendingDecisionsTotal}
        invites={pendingInvites}
        loading={sectionLoading}
        decisionsUnavailable={pendingDecisionsFailed}
        invitesUnavailable={pendingInvitesFailed}
        gates={gates}
        gatesUnavailable={gatesFailed}
      />

      {layout.sections.map((id) => (
        <Fragment key={id}>{sectionNodes[id]}</Fragment>
      ))}

      {/* B21 — Governance Exceptions Pending stays in source, never rendered. */}
      {SHOW_ADMIN_HOME_EXCEPTIONS && showExceptions && (
        <Card data-testid="admin-home-exceptions">
          <CardHeader className="space-y-1 border-b border-slate-100 p-5">
            <h2 className={typography.sectionTitle}>
              Governance Exceptions Pending
            </h2>
            <p className={typography.muted}>
              Exception queue only — not gate approvals.
            </p>
          </CardHeader>
          <CardBody className="p-5">
            {pendingDecisionsTotal === 0 ? (
              <p
                className={typography.muted}
                data-testid="admin-home-exceptions-empty"
              >
                No exceptions pending
              </p>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p
                  className={cn(
                    "text-3xl font-semibold",
                    intentColors.warning.text,
                  )}
                  data-testid="admin-home-exceptions-count"
                >
                  {pendingDecisionsTotal}
                </p>
                <Link
                  to={ADMIN_HOME_GOVERNANCE_EXCEPTIONS_HREF}
                  className={linkOutlineClass}
                  data-testid="admin-home-exceptions-link"
                >
                  Review queue
                </Link>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {sectionLoading && !showWorkspaces && (
        <Card data-testid="admin-home-loading">
          <CardBody className="p-5">
            <InlineLoadingState message="Loading organization sections…" />
          </CardBody>
        </Card>
      )}

      <CustomizeAdminHomeDialog
        isOpen={customizeOpen}
        onClose={closeCustomize}
        layout={layout}
      />

      <InviteMembersDialog
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={() => {
          void loadSections();
        }}
      />

      <WorkspaceCreateModal
        open={createWorkspaceOpen}
        onClose={() => setCreateWorkspaceOpen(false)}
        onCreated={(workspaceId) => {
          setActiveWorkspace(workspaceId);
          setCreateWorkspaceOpen(false);
          void loadSections();
        }}
      />
    </div>
  );
}
